import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, MoreVertical } from 'lucide-react';

interface AudioVisualizerProps {
  audioSrc: string | null;
}

const formatTime = (time: number) => {
  if (!time || isNaN(time)) return '0:00';
  const m = Math.floor(time / 60);
  const s = Math.floor(time % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

const speeds = [0.5, 1, 1.25, 1.5, 2];

const AudioVisualizer: React.FC<AudioVisualizerProps> = ({ audioSrc }) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !audioRef.current.muted;
    setIsMuted(audioRef.current.muted);
  };

  const seek = (value: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = value;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    setIsPlaying(false);
    setCurrentTime(0);
    setShowMenu(false);
  }, [audioSrc]);

  return (
    <div className="max-w-[900px] mx-auto lg:px-3 mt-10 md:mt-0">
      <div className="flex items-center justify-center gap-3 md:flex-row md:gap-6">
        <div className="self-center flex-shrink-0">
          <button
            onClick={togglePlay}
            className="relative flex items-center justify-center w-16 h-16 transition-opacity rounded-full sm:w-18 sm:h-18 md:w-24 md:h-24 lg:w-30 lg:h-30 hover:opacity-95"
            style={{
              background:
                'radial-gradient(circle at 40% 40%, #6B8CFF 0%, #1E3A8A 50%, #0F1E4A 100%)',
              border: '3px solid #c0c0c0',
              boxShadow:
                '0 0 0 2px #707070, 0 3px 10px rgba(0,0,0,0.5), inset 0 -2px 4px rgba(0,0,0,0.6), inset 0 2px 4px rgba(255,255,255,0.2)',
            }}
          >
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  'linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.3) 45%, transparent 50%, rgba(0,0,0,0.3) 95%)',
              }}
            />
            {isPlaying ? (
              <Pause
                size={18}
                fill="white"
                color="white"
                className="relative z-10 sm:size-[24px] md:size-[36px] lg:size-[44px] drop-shadow-lg"
              />
            ) : (
              <Play
                size={18}
                fill="white"
                color="white"
                className="ml-0.5 relative z-10 sm:size-[24px] md:size-[36px] lg:size-[44px] drop-shadow-lg"
              />
            )}
          </button>
        </div>

        <div className="w-full md:flex-1 md:max-w-[320px] lg:max-w-[450px]">
          <audio
            ref={audioRef}
            src={audioSrc ?? undefined}
            onTimeUpdate={() => audioRef.current && setCurrentTime(audioRef.current.currentTime)}
            onLoadedMetadata={() => audioRef.current && setDuration(audioRef.current.duration)}
            onEnded={() => {
              setIsPlaying(false);
            }}
          />

          <div className="relative flex flex-col gap-2 mt-6 md:mt-0">
            <span className="absolute -top-5 left-1/4 sm:left-[40%] md:left-1/3 text-sm md:text-xl whitespace-nowrap text-gray-700">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            <div className="items-center justify-start hidden md:flex">
              <div className="flex justify-center w-full md:flex-1"></div>
              <div className="w-[100px]"></div>
            </div>

            <div className="flex flex-row items-center gap-2 px-0 sm:gap-2 md:gap-4 md:px-2">
              <div
                className="relative w-full flex-1 h-[20px] rounded-full overflow-hidden border-2 border-[#c0c0c0] p-1 md:h-3 md:border-[6px] md:p-[10px]"
                style={{
                  background: '#e0e0e0',
                  boxShadow:
                    '0 0 0 1px #707070, 0 2px 8px rgba(0,0,0,0.4), inset 0 -1px 3px rgba(0,0,0,0.5), inset 0 1px 3px rgba(255,255,255,0.2)',
                }}
              >
                <div
                  className="absolute top-0 left-0 h-full transition-all rounded-full"
                  style={{
                    width: `${(currentTime / duration) * 100}%`,
                    background: 'linear-gradient(to right, #6B8CFF 0%, #1E3A8A 100%)',
                  }}
                />
                <input
                  type="range"
                  min={0}
                  max={duration || 0}
                  value={currentTime}
                  onChange={(e) => seek(Number(e.target.value))}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 flex-shrink-0">
                <button onClick={toggleMute}>
                  {isMuted ? (
                    <VolumeX className="w-4 h-4 text-gray-700 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                  ) : (
                    <Volume2 className="w-4 h-4 text-gray-700 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                  )}
                </button>

                {/* <div ref={menuRef} className="relative">
                  <MoreVertical
                    size={24}
                    className="cursor-pointer sm:size-[16] md:size-[20] text-gray-700"
                    onClick={() => setShowMenu(!showMenu)}
                  />

                  {showMenu && (
                    <div className="absolute right-0 z-10 overflow-hidden text-sm text-black bg-white rounded-md shadow-lg top-8">
                      {speeds.map((speed) => (
                        <button
                          key={speed}
                          onClick={() => {
                            if (audioRef.current)
                              audioRef.current.playbackRate = speed;
                            setShowMenu(false);
                          }}
                          className="block w-full px-4 py-2 text-left hover:bg-gray-100"
                        >
                          {speed}x
                        </button>
                      ))}
                    </div>
                  )}
                </div> */}

                <div ref={menuRef} className="relative">
                  <MoreVertical
                    size={24}
                    className="cursor-pointer size-[14px] sm:size-[16px] md:size-[20px] text-gray-700"
                    onClick={() => setShowMenu(!showMenu)}
                  />
                  {showMenu && (
                    <div className="absolute right-0 z-10 overflow-hidden text-sm text-black bg-white rounded-md shadow-lg top-8">
                      {speeds.map((speed) => (
                        <button
                          key={speed}
                          onClick={() => {
                            if (audioRef.current) audioRef.current.playbackRate = speed;
                            setShowMenu(false);
                          }}
                          className="block w-full px-4 py-2 text-left hover:bg-gray-100"
                        >
                          {speed}x
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center mt-2 md:hidden">
              {/* <span className="text-xs text-gray-700 sm:text-sm whitespace-nowrap">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioVisualizer;
